import { Link } from 'react-router-dom';
import MarketingNav from '../components/MarketingNav';

const SUPPORT_EMAIL = 'team@spirara.com';

const DeleteAccount = () => {
  return (
    <div className="min-h-screen bg-sand-100 font-body text-ink-900">
      <MarketingNav variant="light" current="/delete-account" />

      <main className="mx-auto max-w-2xl px-5 py-16 sm:px-6 sm:py-24">
        <p className="text-xs uppercase tracking-[0.22em] text-saffron-600">Spirara for Business</p>
        <h1 className="mt-3 font-display text-4xl font-light tracking-tight sm:text-5xl">
          Delete your account
        </h1>
        <p className="mt-4 text-base text-ink-700/80 sm:text-lg">
          You can delete your Spirara for Business account and associated data at any time. This
          cannot be undone.
        </p>

        <section className="mt-10">
          <h2 className="font-display text-2xl font-light tracking-tight">How to delete</h2>
          <ol className="mt-4 list-decimal space-y-4 pl-5 text-base text-ink-700/85">
            <li>
              <strong className="font-semibold text-ink-900">In the Spirara for Business app:</strong>{' '}
              open <strong>More</strong>, tap <strong>Delete account</strong>, and confirm. Your
              account is deleted immediately.
            </li>
            <li>
              <strong className="font-semibold text-ink-900">If you cannot open the app:</strong>{' '}
              email{' '}
              <a
                href={`mailto:${SUPPORT_EMAIL}?subject=Delete%20Spirara%20account`}
                className="underline decoration-saffron-500 decoration-2 underline-offset-4"
              >
                {SUPPORT_EMAIL}
              </a>{' '}
              from the email or phone number on your account. Include your business name. We will
              process deletion requests within 7 days.
            </li>
          </ol>
        </section>

        <section className="mt-10">
          <h2 className="font-display text-2xl font-light tracking-tight">What is deleted</h2>
          <p className="mt-4 text-base text-ink-700/85">
            When your account is deleted we remove:
          </p>
          <ul className="mt-3 list-disc space-y-2 pl-5 text-base text-ink-700/85">
            <li>Your sign-in (email, phone, Google, or Apple)</li>
            <li>Your profile and business workspace data tied to that account</li>
            <li>Staff, services, locations, and booking settings you created</li>
            <li>Client records and appointments stored in your Spirara workspace</li>
          </ul>
        </section>

        <section className="mt-10">
          <h2 className="font-display text-2xl font-light tracking-tight">What we may keep</h2>
          <p className="mt-4 text-base text-ink-700/85">
            We may retain limited records required for legal, tax, fraud-prevention, or payment
            compliance (for example transaction logs processed by payment providers). Those records
            are kept only as long as required, typically up to 7 years, then deleted or anonymized.
          </p>
          <p className="mt-4 text-base text-ink-700/85">
            Guest booking details already shared with your business before deletion are not
            automatically removed from that business’s records if they belong to a different account.
          </p>
        </section>

        <div className="mt-10 bg-sand-50 px-5 py-6 ring-1 ring-ink-900/10 sm:px-8 sm:py-8">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
            Request by email
          </p>
          <a
            href={`mailto:${SUPPORT_EMAIL}?subject=Delete%20Spirara%20account`}
            className="mt-3 inline-block font-display text-2xl font-light text-ink-900 underline decoration-saffron-500 decoration-2 underline-offset-4 transition-colors hover:text-ink-700 sm:text-3xl"
          >
            {SUPPORT_EMAIL}
          </a>
          <p className="mt-4 text-sm text-ink-600/70">
            Subject: Delete Spirara account. Include the email or phone on the account and your
            business name.
          </p>
        </div>

        <p className="mt-10 text-sm text-ink-600/70">
          <Link to="/privacy-policy" className="underline decoration-ink-900/30 underline-offset-2 hover:text-ink-900">
            Privacy policy
          </Link>
          {' · '}
          <Link to="/terms-of-service" className="underline decoration-ink-900/30 underline-offset-2 hover:text-ink-900">
            Terms of service
          </Link>
          {' · '}
          <Link to="/support" className="underline decoration-ink-900/30 underline-offset-2 hover:text-ink-900">
            Support
          </Link>
        </p>
      </main>

      <footer className="border-t border-ink-900/10 bg-ink-950 py-10">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-5 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
          <span className="font-display text-2xl font-light text-sand-50">
            Spirara<span className="text-saffron-500">.</span>
          </span>
          <p className="text-xs text-sand-200/50">
            &copy; {new Date().getFullYear()} Spirara. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default DeleteAccount;
